import { Component, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ProductionStatus } from "@enums/productionStatus.enum";
import { GetProductionStatusColor } from "@helpers/getProductionStatusColor";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { MachineService } from "@services/machine.service";
import { SiteService } from "@services/site.service";
import { ToastService } from "angular-toastify";
import { Subscription, forkJoin } from "rxjs";
import { switchMap } from "rxjs/operators";

@Component({
	encapsulation: ViewEncapsulation.None,
	moduleId: module.id,
	selector: "piSiteOverview",
	styleUrls: ["piSiteOverview.css"],
	templateUrl: "piSiteOverview.html"
})
export class PiSiteOverviewComponent {
	breakdownCount = 0;
	breakdownMachines: any[] = [];
	lastRefresh = "";
	machines: any[] = [];
	pageIndex = 0;
	pageSize = 6; // 3 columns × 2 rows
	pagedMachines: any[] = [];
	private paramsSub: Subscription;
	private pollTimer: any;
	prodAct = 0;
	prodAmb = 0;
	prodRed = 0;
	private rotationTimer: any;
	siteId = 0;
	siteName = "";
	statusLookup: any[] = [];
	superRequiredCount = 0;
	supervisorMachines: any[] = [];
	totalPages = 0;

	constructor(private route: ActivatedRoute, private toastService: ToastService, private machineService: MachineService, private siteService: SiteService) {
		this.statusLookup = GetPropertiesFromEnum(ProductionStatus);

		this.paramsSub = this.route.params.pipe(
			switchMap((params: any) => {
				this.siteId = Number(params.id);
				return this.siteService.app_GetAll();
			})
		).subscribe((res: any) => {
			const sites = Array.isArray(res) ? res : (res?.response || []);
			const site = sites.find((s: any) => s.id === this.siteId);
			this.siteName = site?.name || "";
			this.getData();
		});

		this.pollTimer = setInterval(() => {
			this.getData();
		}, 5000);
	}

	formatPlannedDate(date: any): string {
		if (!date) return "";
		const d = new Date(date);
		if (isNaN(d.getTime()) || d.getFullYear() < 2000) return "";
		return d.toLocaleDateString("en-GB");
	}

	getData() {
		if (!this.siteName) return;

		forkJoin({
			edi: this.machineService.app_OpsPlannerFirewallOrders(),
			site: this.machineService.app_OpsPlannerForSite(this.siteId)
		}).subscribe(
			({ site, edi }: any) => {
				const standardMachines = (site.response || [])
					.filter((x: any) => !x.cellNumber?.includes("FW"))
					.map((x: any) => ({ ...x, isFW: false }));

				const fwMachines = (edi.response || [])
					.filter((x: any) => x.machineId !== null && x.cellNumber?.includes("FW") && x.siteName === this.siteName)
					.map((x: any) => ({ ...x, isFW: true }));

				this.machines = [...standardMachines, ...fwMachines];
				this.machines.sort((a, b) => this.getSortPriority(a.productionStatus) - this.getSortPriority(b.productionStatus));

				const tempSuperRequiredCount = this.superRequiredCount;
				this.supervisorMachines = this.machines.filter(x => x.productionStatus == ProductionStatus.AssistanceRequired);
				this.superRequiredCount = this.supervisorMachines.length;
				if (this.superRequiredCount > tempSuperRequiredCount) {
					this.playSupervisorAudio();
				}

				const tempBreakdownCount = this.breakdownCount;
				this.breakdownMachines = this.machines.filter(x => x.productionStatus == ProductionStatus.Breakdown);
				this.breakdownCount = this.breakdownMachines.length;
				if (this.breakdownCount > tempBreakdownCount) {
					this.playBreakdownAudio();
				}

				this.lastRefresh = new Date().toLocaleString();
				this.renderStatusOverview();
				this.refreshPaging();
			},
			(err) => {
				console.log(err);
				this.toastService.error("Something Went Wrong!");
			}
		);
	}

	getFwNextJobsPadded(machine: any): any[] {
		const jobs = machine.firewallOrders?.slice(1, 4) || [];
		const padded = [...jobs];
		while (padded.length < 3) {
			padded.push(null);
		}
		return padded;
	}

	getNextJobs(machine: any): any[] {
		if (!machine.availableStages || machine.availableStages.length === 0) return [];
		return machine.availableStages.slice(0, 3);
	}

	getNextJobsPadded(machine: any): any[] {
		const jobs = this.getNextJobs(machine);
		const padded = [...jobs];
		while (padded.length < 3) {
			padded.push(null);
		}
		return padded;
	}

	getProgressWidth(stage: any): string {
		if (!stage || !stage.quantityRequired || stage.quantityRequired === 0) return "0%";
		const pct = (stage.quantityCompleted / stage.quantityRequired) * 100;
		return Math.min(Math.max(pct, 0), 100) + "%";
	}

	private getSortPriority(status: ProductionStatus): number {
		if (status === ProductionStatus.AssistanceRequired) return 0;
		if (status === ProductionStatus.Active || status === ProductionStatus.Changeover) return 1;
		return 2;
	}

	ngOnDestroy(): void {
		if (this.rotationTimer) clearInterval(this.rotationTimer);
		if (this.pollTimer) clearInterval(this.pollTimer);
		this.paramsSub?.unsubscribe();
	}

	playBreakdownAudio() {
		const audio = new Audio();
		audio.src = "../../../../../../assets/machineBreakdown.mp3";
		audio.load();
		audio.autoplay = true;
		audio.play();
	}

	playSupervisorAudio() {
		const audio = new Audio();
		audio.src = "../../../../../../assets/supervisorRequired.mp3";
		audio.load();
		audio.autoplay = true;
		audio.play();
	}

	private refreshPaging(): void {
		const total = this.machines?.length ?? 0;

		if (total === 0) {
			this.pagedMachines = [];
			this.totalPages = 0;
			this.stopRotation();
			this.pageIndex = 0;
			return;
		}

		this.totalPages = Math.ceil(total / this.pageSize);
		if (this.pageIndex >= this.totalPages) this.pageIndex = 0;

		const start = this.pageIndex * this.pageSize;
		this.pagedMachines = this.machines.slice(start, start + this.pageSize);

		if (total > this.pageSize) {
			this.startRotation();
		} else {
			this.stopRotation();
		}
	}

	renderStatusColor(status: ProductionStatus) {
		return GetProductionStatusColor(status);
	}

	renderStatusName(status: ProductionStatus) {
		if (status == 100) {
			return "SUPERVISOR";
		}
		return this.statusLookup.filter(x => x.id == status)[0].name.toUpperCase();
	}

	renderStatusOverview() {
		this.prodAct = 0;
		this.prodAmb = 0;
		this.prodRed = 0;
		this.machines.forEach(machine => {
			const color = this.renderStatusColor(machine.productionStatus);
			if (color === "#1DFF00") {
				this.prodAct++;
			} else if (color === "#FFA500") {
				this.prodAmb++;
			} else {
				this.prodRed++;
			}
		});
	}

	private startRotation(): void {
		if (this.rotationTimer) return;

		this.rotationTimer = setInterval(() => {
			this.pageIndex = (this.pageIndex + 1) % this.totalPages;
			const start = this.pageIndex * this.pageSize;
			this.pagedMachines = this.machines.slice(start, start + this.pageSize);
		}, 60000);
	}

	private stopRotation(): void {
		if (this.rotationTimer) {
			clearInterval(this.rotationTimer);
			this.rotationTimer = null;
		}
	}
}
